<template>
  <div>
    <div v-if="loading" class="text-center py-4">
      <i class="fas fa-spinner fa-spin"></i> Loading...
    </div>

    <div v-else>
      <div class="mb-4">
        <div class="feature-setting" data-cy="config-enabled">
          <div class="label">Enable Progress Billing</div>
          <ToggleSwitch
            :model-value="config.enabled"
            @update:model-value="updateConfig({ enabled: $event })"
          />
        </div>
        <p class="mb-2">
          Enable progress billing so jobs can be billed in installments rather
          than a single invoice.
        </p>
      </div>

      <div class="mb-4">
        <div class="feature-setting" data-cy="config-tech-override">
          <div class="label">Allow Tech Override</div>
          <ToggleSwitch
            :model-value="config.tech_override_allowed"
            @update:model-value="
              updateConfig({ tech_override_allowed: $event })
            "
          />
        </div>
        <p class="mb-2">
          Allow technicians to override the assigned payment schedule on
          individual jobs from the Tech App.
        </p>
      </div>

      <div class="mb-4">
        <div class="feature-setting" data-cy="config-default-schedule">
          <div class="label">Default Payment Schedule</div>
          <Select
            :model-value="config.default_schedule_id"
            placeholder="None"
            :options="scheduleOptions"
            @update:model-value="updateConfig({ default_schedule_id: $event })"
          />
        </div>
        <p class="mb-2">
          The payment schedule automatically applied to new jobs when progress
          billing is enabled.
        </p>
      </div>

      <div class="mb-4">
        <div class="feature-setting" data-cy="config-default-net-days">
          <div class="label">Default installment interval</div>
          <Select
            :model-value="config.default_net_days"
            :options="netDaysOptions"
            @update:model-value="updateConfig({ default_net_days: $event })"
          />
        </div>
        <p class="mb-2">Default number of days between each installment.</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import ToggleSwitch from "@/components/ToggleSwitch.vue";

interface Schedule {
  id: number;
  name: string;
  is_active: boolean;
}

interface Config {
  id: number;
  enabled: boolean;
  tech_override_allowed: boolean;
  default_schedule_id: number | null;
  default_net_days: number;
}

const store = useStore();

const loading = ref(true);
const config = ref<Config>({
  id: 0,
  enabled: false,
  tech_override_allowed: false,
  default_schedule_id: null,
  default_net_days: 30
});

const props = defineProps<{
  schedules: Schedule[];
}>();

const activeSchedules = computed(() =>
  props.schedules.filter((t) => t.is_active)
);

const scheduleOptions = computed(() =>
  activeSchedules.value.map((t) => ({ label: t.name, value: t.id }))
);

const netDaysOptions = [
  { label: "15 days", value: 15 },
  { label: "30 days", value: 30 },
  { label: "45 days", value: 45 },
  { label: "60 days", value: 60 },
  { label: "90 days", value: 90 }
];

async function loadConfig() {
  loading.value = true;
  config.value = await store.dispatch("fetchProgressBillingConfig");
  loading.value = false;
}

async function updateConfig(changes: Partial<Config>) {
  const result = await store.dispatch("updateProgressBillingConfig", changes);
  if (result) {
    config.value = result;
  }
}

onMounted(loadConfig);
</script>

<style lang="scss" scoped>
.feature-setting {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  align-items: center;
}
</style>
